import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories, categoryMap } from '@/mocks/categories';
import { getComicsByCategory } from '@/mocks/comics';
import { useState } from 'react';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? categoryMap[slug] : undefined;
  const [activeCategory, setActiveCategory] = useState(slug || '');

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
              <i className="ri-emotion-sad-line text-4xl text-primary-500"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-900 mb-2">分类未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">抱歉，你查找的分类不存在</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const comics = getComicsByCategory(slug!);
  const pageTitle = `${category.name}漫画 - 免费${category.name}漫画全集在线免费阅读`;
  const pageDescription = `星球漫画${category.name}分类 - ${category.description}。海量免费${category.name}漫画，下拉式阅读体验，免VIP看全集，好看的免费${category.name}漫画推荐。`;
  const pageKeywords = `免费${category.name}漫画,${category.name}漫画免费,免费${category.name}漫画全集,在线免费${category.name}漫画,星球漫画${category.name}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDescription,
    url: `https://www.xingqiumanhua.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '星球漫画',
      url: 'https://www.xingqiumanhua.com',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <link rel="canonical" href={`https://www.xingqiumanhua.com/category/${slug}`} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:url" content={`https://www.xingqiumanhua.com/category/${slug}`} />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: `${category.name}漫画` },
            ]}
          />

          <div className="px-4 md:px-6 py-6 md:py-10">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className={`${category.icon} text-lg`}></i>
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-heading text-foreground-900">{category.name}漫画</h1>
                <p className="text-sm text-foreground-500">{category.description}</p>
              </div>
            </div>

            <div className="mt-6 mb-6">
              <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => setActiveCategory(cat.slug)}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            {comics.length > 0 ? (
              <div data-product-shop className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {comics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-inbox-line text-3xl text-primary-500"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无漫画，请查看其他分类</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}